import React, { useEffect, useMemo, useState, useRef, useCallback } from 'react';
import { Navigate } from 'react-router-dom';

import { useTheme } from '../theme/ThemeContext';
import { usePermissions } from '../context/PermissionContext';
import useProcedureStore from '../stores/procedureStore';
import useDoctorRateStore from '../stores/doctorRateStore';
import useOrgSettingsStore from '../stores/orgSettingsStore';
import { fetchStaffList } from '../services/api/staffApi';
import FeesBillingTabs from '../components/settings/FeesBillingTabs';
import AdminPageHeader from '../components/common/AdminPageHeader';
import DoctorPicker from '../components/procedures/DoctorPicker';
import {
  PROCEDURE_CATEGORIES,
  PROCEDURE_CATEGORY_LABELS,
  PROCEDURE_CATEGORY_SHORT_LABELS,
} from '../constants/procedures';
import './DoctorRateOverridesPage.css';

const DEBOUNCE_MS = 500;

function formatCurrency(n) {
  const num = Number(n || 0);
  return `₹${num.toFixed(2)}`;
}

/**
 * Inline override input for ONE procedure row.
 *
 * Debounces 500ms after the user stops typing, then upserts via the store.
 * Shows a small "Saved" indicator for 1.5s on success, or an error chip.
 * ✕ button clears the override (deletes the row so clinic default applies).
 */
function OverrideInput({ procedureId, currentOverride, onSet, onClear }) {
  const [value, setValue] = useState(
    currentOverride !== undefined ? String(currentOverride) : ''
  );
  const [status, setStatus] = useState(null); // 'saving' | 'saved' | 'error'
  const [errMsg, setErrMsg] = useState('');
  const timerRef = useRef(null);

  // Sync from upstream when the doctor changes / rates are fetched
  useEffect(() => {
    setValue(currentOverride !== undefined ? String(currentOverride) : '');
    setStatus(null);
  }, [currentOverride]);

  const triggerSave = useCallback(
    async (raw) => {
      const trimmed = (raw || '').trim();
      // Empty input → noop (use the ✕ button to clear, to avoid accidental wipes)
      if (trimmed === '') return;
      const num = Number(trimmed);
      if (Number.isNaN(num) || num < 0) {
        setStatus('error');
        setErrMsg('Enter a number ≥ 0');
        return;
      }
      setStatus('saving');
      setErrMsg('');
      const result = await onSet(procedureId, num);
      if (result?.success) {
        setStatus('saved');
        setTimeout(() => setStatus((s) => (s === 'saved' ? null : s)), 1500);
      } else {
        setStatus('error');
        setErrMsg(result?.error || 'Save failed');
      }
    },
    [onSet, procedureId]
  );

  const onChange = (e) => {
    const next = e.target.value;
    setValue(next);
    setStatus(null);
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => triggerSave(next), DEBOUNCE_MS);
  };

  const onBlur = () => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      triggerSave(value);
    }
  };

  const handleClear = async () => {
    setStatus('saving');
    setErrMsg('');
    const result = await onClear(procedureId);
    if (result?.success) {
      setValue('');
      setStatus(null);
    } else {
      setStatus('error');
      setErrMsg(result?.error || 'Clear failed');
    }
  };

  return (
    <div className="drop-override">
      <span className="drop-rupee">₹</span>
      <input
        type="number"
        min="0"
        step="0.01"
        value={value}
        onChange={onChange}
        onBlur={onBlur}
        placeholder="—"
        className={`drop-input ${status === 'error' ? 'drop-input-err' : ''}`}
      />
      {currentOverride !== undefined && (
        <button
          type="button"
          className="drop-clear"
          onClick={handleClear}
          title="Remove override — clinic default will apply"
          aria-label="Remove override"
        >
          ✕
        </button>
      )}
      {status === 'saving' && <span className="drop-status drop-status-saving">…</span>}
      {status === 'saved' && <span className="drop-status drop-status-ok">Saved</span>}
      {status === 'error' && (
        <span className="drop-status drop-status-err" title={errMsg}>!</span>
      )}
    </div>
  );
}

export default function DoctorRateOverridesPage() {
  const { isDark } = useTheme();
  const { hasPermission } = usePermissions();

  // Permission gate is applied AFTER all hooks below (Rules of Hooks).

  const {
    procedures,
    selectedCategory,
    isLoading: proceduresLoading,
    error: proceduresError,
    fetchProcedures,
    setCategory,
  } = useProcedureStore();

  const {
    selectedDoctorId,
    rates,
    isLoading: ratesLoading,
    error: ratesError,
    selectDoctor,
    setRate,
    clearRate,
  } = useDoctorRateStore();

  const [doctors, setDoctors] = useState([]);
  const [doctorsLoading, setDoctorsLoading] = useState(true);
  const [doctorsErr, setDoctorsErr] = useState(null);

  // When the clinic skips payment for ALL procedures, per-doctor pricing is
  // moot. Show this page greyed/read-only with a notice to disable skip.
  const skipPaymentAll = useOrgSettingsStore((s) => s.skipPaymentAll);
  const fetchOrgSettings = useOrgSettingsStore((s) => s.fetchProcedurePaymentSettings);
  useEffect(() => { fetchOrgSettings(); }, [fetchOrgSettings]);
  const lockedStyle = skipPaymentAll
    ? { opacity: 0.5, pointerEvents: 'none', userSelect: 'none' }
    : undefined;

  // ── Fetch doctors + full active catalog on mount ─────────────
  useEffect(() => {
    let alive = true;
    (async () => {
      setDoctorsLoading(true);
      const result = await fetchStaffList();
      if (!alive) return;
      if (result.success) {
        const onlyDoctors = (result.data || []).filter(
          (m) =>
            (m.role || '').toUpperCase() === 'DOCTOR' &&
            (m.status || 'ACTIVE').toUpperCase() === 'ACTIVE'
        );
        setDoctors(onlyDoctors);
      } else {
        setDoctorsErr(result.error);
      }
      setDoctorsLoading(false);
    })();
    return () => { alive = false; };
  }, []);

  // Load the full catalog on mount if we don't already have it.
  // Do NOT reset the shared selectedCategory / includeInactive — that's the
  // catalog tab's state; respecting it keeps the filter consistent when the
  // user toggles between "Procedure Catalog" and "Doctor Rate Overrides".
  useEffect(() => {
    if (procedures.length === 0 && !proceduresLoading) {
      fetchProcedures();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── Group catalog by category for rendering ──────────────────
  const grouped = useMemo(() => {
    const g = {};
    for (const c of PROCEDURE_CATEGORIES) g[c.value] = [];
    for (const p of procedures) {
      if (p.active === false) continue;
      if (!g[p.category]) g[p.category] = [];
      g[p.category].push(p);
    }
    // Sort each group by name for stable rendering
    for (const k of Object.keys(g)) g[k].sort((a, b) => (a.name || '').localeCompare(b.name || ''));
    return g;
  }, [procedures]);

  // Per-category counts stay accurate because ``grouped`` is computed
  // from the FULL catalog. The tab-filter only changes what we render.
  const counts = useMemo(() => {
    const c = { all: 0 };
    for (const cat of PROCEDURE_CATEGORIES) c[cat.value] = 0;
    for (const p of procedures) {
      if (p.active === false) continue;
      c.all += 1;
      if (c[p.category] !== undefined) c[p.category] += 1;
    }
    return c;
  }, [procedures]);

  const rateTabs = [
    { value: 'all', label: 'All' },
    ...PROCEDURE_CATEGORIES.map((c) => ({
      value: c.value,
      label: PROCEDURE_CATEGORY_SHORT_LABELS[c.value] || c.label,
    })),
  ];

  const visibleCategories = PROCEDURE_CATEGORIES.filter((c) => {
    if (selectedCategory !== 'all' && c.value !== selectedCategory) return false;
    return grouped[c.value] && grouped[c.value].length > 0;
  });

  const bg = isDark
    ? 'linear-gradient(135deg, #0E0E0F 0%, #1a1a2e 50%, #0E0E0F 100%)'
    : 'linear-gradient(135deg, #f8fafc 0%, #eef2ff 50%, #f8fafc 100%)';

  const isLoading = proceduresLoading || ratesLoading || doctorsLoading;

  // Permission gate (all hooks are called above; safe to early-return now)
  if (!hasPermission('MANAGE_PROCEDURE_RATES')) {
    return <Navigate to="/access-denied" replace />;
  }

  return (
    <div className="drop-page" style={{ background: bg }}>
      <AdminPageHeader />

      <main className="drop-main">
        <div className="drop-title-row">
          <h1 className="drop-page-title" style={{ color: isDark ? '#f5f5f7' : '#111827' }}>
            ₹ Doctor Rate Overrides
          </h1>
          <p className="drop-page-subtitle" style={{ color: isDark ? '#9ca3af' : '#6b7280' }}>
            Per-doctor procedure pricing · overrides the clinic default when set.
          </p>
        </div>
        <FeesBillingTabs active="rates" />

        {skipPaymentAll && (
          <div
            style={{
              margin: '0.75rem 0',
              padding: '0.6rem 0.85rem',
              borderRadius: 8,
              fontSize: '0.85rem',
              background: isDark ? 'rgba(180,83,9,0.22)' : '#fef3c7',
              color: isDark ? '#fde68a' : '#92400e',
              border: `1px solid ${isDark ? 'rgba(180,83,9,0.5)' : '#fcd34d'}`,
            }}
          >
            “Skip payment for all procedures” is enabled for this
            organisation, so no procedure is charged. Per-doctor rate
            overrides are paused — ask an administrator to turn off skip
            payment to use this feature. Existing overrides below are kept
            and will apply again once it is turned off.
          </div>
        )}

        {/* Picker + rates content — greyed & read-only while skip-all is on. */}
        <div style={lockedStyle} aria-disabled={skipPaymentAll || undefined}>
        {/* Doctor picker (custom combobox with avatars + search) */}
        <div className="drop-picker">
          <label className="drop-picker-label">Doctor</label>
          <DoctorPicker
            doctors={doctors}
            selectedId={selectedDoctorId}
            onChange={(id) => selectDoctor(id)}
            isLoading={doctorsLoading}
          />
          {doctorsErr && <div className="drop-err">{doctorsErr}</div>}
        </div>

        {/* Category filter — always visible so the admin can see how many
            procedures live in each category before (or without) picking a
            doctor. Counts come from the full active catalog. */}
        <div className="drop-tabs" role="tablist">
          {rateTabs.map((t) => (
            <button
              key={t.value}
              role="tab"
              aria-selected={selectedCategory === t.value}
              className={`drop-tab ${selectedCategory === t.value ? 'drop-tab-active' : ''}`}
              onClick={() => setCategory(t.value)}
            >
              {t.label}
              <span className="drop-tab-count">{counts[t.value] ?? 0}</span>
            </button>
          ))}
        </div>

        {/* Main content — ALWAYS renders the filtered catalog so the pill
            tabs show visible feedback even before a doctor is selected.
            The Override column is only populated once a doctor is picked;
            otherwise we show a hint in that cell. */}
        {proceduresError ? (
          <div className="drop-state drop-err">{proceduresError}</div>
        ) : ratesError ? (
          <div className="drop-state drop-err">{ratesError}</div>
        ) : isLoading ? (
          <div className="drop-state">Loading…</div>
        ) : visibleCategories.length === 0 && selectedCategory === 'all' ? (
          <div className="drop-state">
            🗂 No active procedures in the catalog yet. Create some in the Procedure Catalog first.
          </div>
        ) : (
          <div className="drop-rates">
            {!selectedDoctorId && (
              <div className="drop-hint">
                👆 Pick a doctor above to set per-doctor overrides. Clinic defaults are shown below.
              </div>
            )}

            {selectedDoctorId && (
              <div className="drop-legend">
                <span>
                  <span className="drop-dot drop-dot-override" /> Override set
                </span>
                <span>
                  <span className="drop-dot drop-dot-default" /> Clinic default
                </span>
              </div>
            )}

            {visibleCategories.length === 0 ? (
              <div className="drop-state">
                No {PROCEDURE_CATEGORY_LABELS[selectedCategory] || ''} procedures in the catalog.
              </div>
            ) : visibleCategories.map((cat) => (
              <section key={cat.value} className="drop-section">
                <h2 className="drop-section-title">
                  {PROCEDURE_CATEGORY_LABELS[cat.value]}
                  <span className="drop-section-count">{grouped[cat.value].length}</span>
                </h2>
                <table className="drop-table">
                  <thead>
                    <tr>
                      <th>Procedure</th>
                      <th>Clinic Default</th>
                      <th>Override</th>
                    </tr>
                  </thead>
                  <tbody>
                    {grouped[cat.value].map((p) => {
                      const override = rates[p.procedure_id];
                      const hasOverride = override !== undefined;
                      return (
                        <tr
                          key={p.procedure_id}
                          className={hasOverride && selectedDoctorId ? 'drop-row drop-row-override' : 'drop-row'}
                        >
                          <td>
                            <div className="drop-name">{p.name}</div>
                            <div className="drop-subname">
                              {p.procedure_id}
                              {p.specialty && ` · ${p.specialty}`}
                            </div>
                          </td>
                          <td className="drop-default">
                            {formatCurrency(p.default_cost)}
                          </td>
                          <td>
                            {selectedDoctorId ? (
                              <OverrideInput
                                procedureId={p.procedure_id}
                                currentOverride={
                                  hasOverride ? Number(override.override_cost) : undefined
                                }
                                onSet={setRate}
                                onClear={clearRate}
                              />
                            ) : (
                              <span className="drop-default-placeholder">
                                Pick a doctor to override
                              </span>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </section>
            ))}
          </div>
        )}
        </div>
      </main>
    </div>
  );
}
